import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FilePlus, FileEdit, Send, EyeOff, Archive, FilePen, Trash2, Landmark, Paperclip, FileMinus, SquareDashed } from 'lucide-react';
import { getLegalAct, getLegalActAudit, getLegalActsAuditAll, } from '../../../api/legalActs';
import { PageHeader } from '../../../components/layout/PageHeader';
import { DataTable } from '../../../components/ui/DataTable';
import { Pagination } from '../../../components/ui/Pagination';
import { LEGAL_ACT_AUDIT_ACTION_LABELS, LEGAL_ACT_TYPE_LABELS, } from '../../../types/legalAct';
const ACTION_ICONS = {
    created: _jsx(FilePlus, { size: 15, className: "text-gray-500" }),
    updated: _jsx(FileEdit, { size: 15, className: "text-blue-500" }),
    published: _jsx(Send, { size: 15, className: "text-emerald-600" }),
    unpublished: _jsx(EyeOff, { size: 15, className: "text-amber-600" }),
    archived: _jsx(Archive, { size: 15, className: "text-blue-500" }),
    document_replaced: _jsx(FilePen, { size: 15, className: "text-orange-500" }),
    deleted: _jsx(Trash2, { size: 15, className: "text-red-500" }),
    transmission_updated: _jsx(Landmark, { size: 15, className: "text-indigo-500" }),
    document_added: _jsx(Paperclip, { size: 15, className: "text-gray-500" }),
    document_removed: _jsx(FileMinus, { size: 15, className: "text-red-500" }),
    document_redacted: _jsx(SquareDashed, { size: 15, className: "text-gray-800" }),
};
function MetadataDisplay({ metadata }) {
    const entries = Object.entries(metadata);
    if (entries.length === 0)
        return _jsx("span", { className: "text-gray-400", children: "-" });
    return (_jsx("ul", { className: "space-y-0.5", children: entries.map(([k, v]) => (_jsxs("li", { className: "text-xs text-gray-500", children: [_jsx("span", { className: "font-mono text-gray-400", children: k }), " : ", String(v)] }, k))) }));
}
export default function LegalActAudit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isPerActAudit = Boolean(id);
    const [page, setPage] = useState(1);
    const [actionFilter, setActionFilter] = useState('');
    const [userIdFilter, setUserIdFilter] = useState('');
    const params = useMemo(() => {
        const p = { page, limit: 50 };
        if (actionFilter)
            p.action = actionFilter;
        if (userIdFilter)
            p.user_id = userIdFilter;
        return p;
    }, [page, actionFilter, userIdFilter]);
    const { data: actData } = useQuery({
        queryKey: ['admin-legal-act', id],
        queryFn: () => getLegalAct(Number(id)),
        enabled: isPerActAudit,
    });
    const { data: perActLogs, isLoading: isPerActLoading } = useQuery({
        queryKey: ['admin-legal-act-audit', id],
        queryFn: () => getLegalActAudit(Number(id)),
        enabled: isPerActAudit,
    });
    const { data: globalLogs, isLoading: isGlobalLoading } = useQuery({
        queryKey: ['admin-legal-acts-audit-all', params],
        queryFn: () => getLegalActsAuditAll(params),
        enabled: !isPerActAudit,
    });
    const perActColumns = [
        {
            key: 'action',
            header: 'Action',
            render: (l) => (_jsxs("span", { className: "flex items-center gap-2 text-sm", children: [ACTION_ICONS[l.action], LEGAL_ACT_AUDIT_ACTION_LABELS[l.action]] })),
        },
        {
            key: 'performed_at',
            header: 'Date',
            render: (l) => new Date(l.performed_at).toLocaleString('fr-FR'),
        },
        {
            key: 'performed_by_user_id',
            header: 'Agent',
            render: (l) => l.performed_by_user_id > 0 ? (l.performed_by_name ?? `Agent n° ${l.performed_by_user_id}`) : 'Système',
        },
        {
            key: 'metadata',
            header: 'Détails',
            render: (l) => _jsx(MetadataDisplay, { metadata: l.metadata }),
        },
    ];
    const globalColumns = [
        {
            key: 'performed_at',
            header: 'Date',
            render: (l) => new Date(l.performed_at).toLocaleString('fr-FR'),
        },
        {
            key: 'action',
            header: 'Action',
            render: (l) => (_jsxs("span", { className: "flex items-center gap-2 text-sm", children: [ACTION_ICONS[l.action], LEGAL_ACT_AUDIT_ACTION_LABELS[l.action]] })),
        },
        {
            key: 'act',
            header: 'Acte',
            render: (l) => (_jsxs("div", { children: [_jsx("div", { className: "text-xs font-mono text-gray-400", children: l.act.reference_number }), _jsx("div", { className: "text-sm truncate max-w-xs", children: l.act.title }), _jsx("div", { className: "text-xs text-gray-400", children: LEGAL_ACT_TYPE_LABELS[l.act.type] })] })),
        },
        {
            key: 'performed_by_user_id',
            header: 'Agent',
            render: (l) => l.performed_by_user_id > 0 ? (l.performed_by_name ?? `Agent n° ${l.performed_by_user_id}`) : 'Système',
        },
        {
            key: 'metadata',
            header: 'Détails',
            render: (l) => _jsx(MetadataDisplay, { metadata: l.metadata }),
        },
    ];
    if (isPerActAudit) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "Historique de l'acte", subtitle: actData ? `${actData.reference_number} : ${actData.title}` : undefined, actions: _jsxs("button", { onClick: () => navigate('/commune/legal-acts'), className: "px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-1", children: [_jsx(ArrowLeft, { size: 14 }), " Retour"] }) }), _jsx(DataTable, { columns: perActColumns, data: perActLogs ?? [], isLoading: isPerActLoading, emptyMessage: "Aucune entr\u00E9e." })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "Journal d'audit de l'affichage l\u00E9gal", subtitle: globalLogs ? `${globalLogs.meta.total} entrée(s)` : undefined, actions: _jsxs("div", { className: "flex gap-2", children: [_jsxs("select", { value: actionFilter, onChange: (e) => { setActionFilter(e.target.value); setPage(1); }, className: "px-3 py-2 border rounded-lg text-sm", children: [_jsx("option", { value: "", children: "Toutes les actions" }), Object.entries(LEGAL_ACT_AUDIT_ACTION_LABELS).map(([k, l]) => (_jsx("option", { value: k, children: l }, k)))] }), _jsx("input", { type: "number", min: "1", value: userIdFilter, onChange: (e) => { setUserIdFilter(e.target.value); setPage(1); }, placeholder: "ID agent", className: "px-3 py-2 border rounded-lg text-sm w-32" }), _jsxs("button", { onClick: () => navigate('/commune/legal-acts'), className: "px-3 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-1", children: [_jsx(ArrowLeft, { size: 14 }), " Retour aux actes"] })] }) }), _jsx(DataTable, { columns: globalColumns, data: globalLogs?.data ?? [], isLoading: isGlobalLoading, emptyMessage: "Aucune entr\u00E9e." }), globalLogs && _jsx(Pagination, { page: page, totalPages: globalLogs.meta.pages, onChange: setPage })] }));
}
